/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import styled from '@emotion/styled'
import type Bluebird from 'bluebird'
import moment from 'moment'
import { calculateRegularShiftHours } from 'domains/shifts/components/utils/calculateRegularShiftHours'
import { type ApiResponse } from 'legacy/common/types'
import { type WithApiInjectedProps } from 'legacy/common/withApi'
import { type ShiftsRouteQuery } from 'domains/shifts/components/types'
import { type WarehouseEarlyPickingHour } from 'domains/shifts/components/utils/fetchEarlyHoursForWarehouse'
import ShiftsHeader from 'domains/shifts/components/ShiftsHeader'
import LoadingDots from 'legacy/components/LoadingDots'
import EDIT_PENCIL from 'assets/edit-pencil.svg'
import { WeeklyStaffingLevelsRow } from 'domains/shifts/components/components/staffing-levels/WeeklyStaffingLevelsRow'
import { computeStaffingLevels } from 'domains/shifts/components/components/staffing-levels/utils/computeStaffingLevels'
import {
  type WeeklyStaffingApiResponse,
  type WeeklyStaffingData,
  type WeeklyStaffingDataCache,
} from 'domains/shifts/components/components/weekly-staffing/utils/types'
import { getHoursLimitsForWeek } from 'domains/shifts/components/components/weekly-staffing/utils/getHoursLimitsForWeek'
import { getHoursOffsetsPerDay } from 'domains/shifts/components/components/weekly-staffing/utils/getHoursOffsetsPerDay'
import {
  locationDeliveryHoursQuery,
  type LocationDeliveryHours,
} from 'domains/shifts/components/queries/locationsDeliveryHoursQuery'
import { calculateActualStoreHours } from 'domains/shifts/components/utils/shiftsUtils'

const EditContainer = styled.div({
  position: 'relative',
})

/* eslint-disable @emotion/syntax-preference */
const OverlayTrigger = styled.div`
  position: absolute;
  top: 0;
  left: 15px;
  height: 100%;
  width: calc(100% - 15px);

  :hover {
    .edit-overlay {
      visibility: visible;
    }
  }
`
/* eslint-enable @emotion/syntax-preference */
const EditOverlay = styled.div({
  visibility: 'hidden',
  position: 'absolute',
  top: '-1px',
  left: 0,
  height: '100%',
  width: '100%',
  border: `1px solid #343538`,
  boxSizing: 'border-box',
  borderRadius: '4px',
  cursor: 'pointer',
})

const EditIcon = styled.div({
  position: 'absolute',
  width: '34px',
  height: '34px',
  padding: '5px',
  top: '5px',
  left: '88px',
  backgroundColor: '#e8e9eb',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '50px',
})
interface Props extends WithApiInjectedProps {
  weeklyStaffingData: WeeklyStaffingDataCache
  warehouseEarlyHours: WarehouseEarlyPickingHour
  handleNavToDay: (date: string) => void
  zoneOffset: number
  zone: string
  signalWeekIsLoading: (isWeekLoading: boolean) => void
  setWeeklyStaffingData: (WeeklyStaffingData: WeeklyStaffingDataCache) => void
  useDevicePrioritization: boolean
  warehouseId: string
  partnerId: string
  query: ShiftsRouteQuery
  isRunnerDevicePrioritizationEnabled: boolean
}

interface State {
  isLoading: boolean
  weeklyStaffing: WeeklyStaffingData
  weeklyActualHours: LocationDeliveryHours[]
}

export class WeeklyStaffing extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      isLoading: false,
      weeklyStaffing: null,
      weeklyActualHours: null,
    }
  }

  componentDidMount() {
    this.fetchWeeklyData()
  }

  componentDidUpdate(prevProps) {
    if (this.props.query.week !== prevProps.query.week) {
      this.fetchWeeklyData()
    }
  }

  fetchPromise: Bluebird<ApiResponse<WeeklyStaffingApiResponse, {}>> | undefined

  componentWillUnmount() {
    if (this.fetchPromise && this.fetchPromise.isPending()) {
      this.fetchPromise.cancel()
    }
  }

  getRequestParams() {
    const {
      query: { warehouse_location_id, date, week },
      partnerId,
      warehouseId,
    } = this.props

    return {
      partner_id: partnerId,
      warehouse_id: warehouseId,
      warehouse_location_id,
      date,
      week,
    }
  }

  async fetchWeeklyData() {
    const { weeklyStaffingData, signalWeekIsLoading, setWeeklyStaffingData } = this.props
    const { week } = this.getRequestParams()

    const cachedResponse = weeklyStaffingData[week]
    if (cachedResponse) {
      this.setState(cachedResponse)
      return
    }

    this.setState({ isLoading: true })
    signalWeekIsLoading(true)

    const [resWeeklyStaffing, resWeeklyActualHours] = await Promise.all([
      this.fetchWeeklyStaffing(),
      this.fetchWeeklyActualHours(),
    ])
    setWeeklyStaffingData({
      ...weeklyStaffingData,
      [week]: {
        weeklyStaffing: resWeeklyStaffing,
        weeklyActualHours: resWeeklyActualHours,
      },
    })

    this.setState({
      isLoading: false,
      weeklyStaffing: resWeeklyStaffing,
      weeklyActualHours: resWeeklyActualHours,
    })
    signalWeekIsLoading(false)
  }

  async fetchWeeklyStaffing() {
    const { partner_id, warehouse_id, warehouse_location_id, week } = this.getRequestParams()
    const { api, warehouseEarlyHours, useDevicePrioritization } = this.props

    if (!week || !warehouse_location_id || !partner_id || !warehouse_id) {
      // TODO: report error for missing params
      return
    }

    if (this.fetchPromise && this.fetchPromise.isPending()) {
      this.fetchPromise.cancel()
    }

    this.fetchPromise = api.get(
      `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts/fetch_weekly_staffing`,
      {
        query: {
          week: week,
          warehouse_location_id,
          device_prioritization_enabled: useDevicePrioritization,
        },
      }
    )
    const result = await this.fetchPromise

    const regularHours = result.data.delivery_hours.map(deliveryHours =>
      calculateRegularShiftHours({
        deliveryHours,
        warehouseEarlyHours,
      })
    )

    return {
      shoppers: result.data.shoppers,
      staffingLevels: result.data.staffing_levels,
      regularHours,
    }
  }

  async fetchWeeklyActualHours() {
    const { warehouse_location_id, week } = this.getRequestParams()

    if (!week || !warehouse_location_id) return

    const dates = []
    const curr = moment(week)
    for (let i = 0; i < 7; i++) {
      dates.push(curr.format('YYYY-MM-DD'))
      curr.add(1, 'd')
    }

    const response = await locationDeliveryHoursQuery({
      id: warehouse_location_id,
      dates,
    })

    return response.warehouseLocation.deliveryHours
  }

  render() {
    const { isLoading, weeklyStaffing, weeklyActualHours } = this.state
    const {
      warehouseEarlyHours,
      handleNavToDay,
      zoneOffset,
      zone,
      useDevicePrioritization,
      isRunnerDevicePrioritizationEnabled,
    } = this.props

    const { week } = this.getRequestParams()
    const date = moment(week)

    if (isLoading || !weeklyStaffing) {
      return <LoadingDots centered />
    }

    const hoursLimits = getHoursLimitsForWeek(weeklyStaffing.regularHours)
    const hoursOffsets = getHoursOffsetsPerDay(weeklyStaffing.regularHours, hoursLimits)
    const longestDay = {
      // longest day has the earliest start time and latest end time in the week
      start: moment(weeklyStaffing.regularHours[0].start)
        .startOf('day')
        .add(hoursLimits.start, 'hours')
        .toDate(),
      // hoursLimit is offset from the start
      end: moment(weeklyStaffing.regularHours[0].start)
        .startOf('day')
        .add(hoursLimits.end, 'hours')
        .toDate(),
    }

    return (
      <React.Fragment>
        <ShiftsHeader
          zoneOffset={zoneOffset}
          zone={zone}
          isWeeklyView={true}
          regularStoreOpen={longestDay.start}
          regularStoreClose={longestDay.end}
        />

        {weeklyStaffing.shoppers.map((shoppers, idx) => {
          const staffingLevels = weeklyStaffing.staffingLevels[idx]
          const regularHours = weeklyStaffing.regularHours[idx]

          const actualHours = weeklyActualHours[idx]
          const { actualStoreOpen, actualStoreClose } = calculateActualStoreHours(
            warehouseEarlyHours,
            regularHours,
            actualHours
          )

          const label = date.format('ddd')
          const sublabel = date.format('D MMM')
          const formattedDate = date.format('YYYY-MM-DD')
          const navigateToDay = () => handleNavToDay(formattedDate)
          date.add(1, 'd')

          const { openingHoursOffset, closingHoursOffset } = hoursOffsets[idx]

          const computedStaffingLevels = computeStaffingLevels({
            staffingLevels,
            shoppers,
            storeHours: {
              actualStoreOpen,
              actualStoreClose,
            },
            useDevicePrioritization,
            isRunnerDevicePrioritizationEnabled,
          })

          return (
            <EditContainer key={idx}>
              <WeeklyStaffingLevelsRow
                label={label}
                sublabel={sublabel}
                staffingLevels={computedStaffingLevels}
                openingHourOffset={openingHoursOffset}
                closingHourOffset={closingHoursOffset}
              />
              <OverlayTrigger onClick={navigateToDay}>
                <EditOverlay className="edit-overlay">
                  <EditIcon>
                    <img src={EDIT_PENCIL} />
                  </EditIcon>
                </EditOverlay>
              </OverlayTrigger>
            </EditContainer>
          )
        })}
      </React.Fragment>
    )
  }
}
